import { useCallback } from 'react';
import { useSaveUserDetailsMutation } from '../queries/saveUserDetails.mutation.generated';
import { useRefreshUserClaimsMutation } from '../queries/refreshClaims.mutation.generated';
import { useTerminateUserMutation } from '../queries/terminateUser.mutation.generated';
import { useReinstateUserMutation } from '../queries/reinstateUser.mutation.generated';
import { useUpsertAllowedRolesMutation } from '../queries/upsertAllowedRoles.mutation.generated';
import { useRemoveAllowedRoleMutation } from '../queries/removeAllowedRole.mutation.generated';
import { useGetRoleTypesQuery } from '../queries/getRoleTypes.query.generated';
import { useInsertDriverMutation } from '../../drivers/queries/insertDriver.mutation.generated';
import { useUpdateDriverConfigMutation } from '../../drivers/queries/updateDriverConfig.mutation.generated';
import { GetUserDetailsDocument } from '../queries/getUserDetails.query.generated';
import { useStore } from '@store';
import { useRoleSwap } from './useRoleSwap';
import type { MobilityAttribute } from '@utils/productMapper';

function stamp() {
  const now = new Date().toISOString();
  const email = useStore.getState().user?.profile?.email || 'portal';
  return { created_by: email, updated_by: email, created_at: now, updated_at: now };
}

function refetchUser(userId: number) {
  return { refetchQueries: [{ query: GetUserDetailsDocument, variables: { userId } }] };
}

type Attributes = Partial<Record<MobilityAttribute, boolean>>;

/**
 * Centralized hook for all role and driver access mutations.
 *
 * Handles default_role changes, usertoroles sync, claims refresh,
 * driver record creation, driver role grant/revoke, driver attribute
 * toggling, and user deactivation/reactivation.
 */
export function useRoleManagement() {
  const [updateUser, { loading: updateLoading }] = useSaveUserDetailsMutation();
  const [refreshClaims, { loading: refreshLoading }] = useRefreshUserClaimsMutation();
  const [terminateUser, { loading: deactivateLoading }] = useTerminateUserMutation();
  const [reinstateUser, { loading: reactivateLoading }] = useReinstateUserMutation();
  const [upsertRole] = useUpsertAllowedRolesMutation();
  const [removeRole] = useRemoveAllowedRoleMutation();
  const [insertDriver] = useInsertDriverMutation();
  const [updateDriverConfig] = useUpdateDriverConfigMutation();
  const { data: roleTypesData } = useGetRoleTypesQuery({ fetchPolicy: 'cache-first' });
  const { syncRoles } = useRoleSwap();

  const driverRoleId = roleTypesData?.roletypes?.find(rt => rt.name === 'driver')?.id ?? null;

  /** Set a user's default_role and sync usertoroles + claims. */
  const assignRole = useCallback(
    async (userId: number, role: string) => {
      await updateUser({
        variables: { userId, userObj: { default_role: role } },
        ...refetchUser(userId),
      });
      await syncRoles(userId);
      await refreshClaims({ variables: { userId } });
    },
    [updateUser, syncRoles, refreshClaims],
  );

  /**
   * Grant mobile app (driver) access to a user.
   *
   * If no driver record exists, creates one with status='offline', tax_class='lite',
   * applies any pre-configured attributes, and adds the driver role to usertoroles.
   *
   * If a driver record already exists, just ensures the driver role is in usertoroles.
   */
  const grantDriverAccess = useCallback(
    async (userId: number, hasDriverRecord: boolean, initialAttributes?: Partial<Record<string, boolean>>) => {
      if (!driverRoleId) throw new Error('Driver role type not found');

      if (!hasDriverRecord) {
        const now = new Date().toISOString();
        const config: Record<string, unknown> = {};
        if (initialAttributes && Object.keys(initialAttributes).length > 0) {
          config.attributes = initialAttributes;
        }

        await insertDriver({
          variables: {
            driverInput: {
              user_id: userId,
              status: 'offline',
              tax_class: 'lite',
              config: Object.keys(config).length > 0 ? config : undefined,
              created_at: now,
              updated_at: now,
            },
          },
        });
      }

      await upsertRole({
        variables: {
          allowedRolesObj: [{ user_id: userId, role_id: driverRoleId, ...stamp() }],
        },
        ...refetchUser(userId),
      });
    },
    [driverRoleId, upsertRole, insertDriver],
  );

  /** Remove the driver role from usertoroles (revokes mobile app access). */
  const revokeDriverAccess = useCallback(
    async (userId: number) => {
      if (!driverRoleId) throw new Error('Driver role type not found');
      await removeRole({
        variables: { userId, roleId: driverRoleId },
        ...refetchUser(userId),
      });
    },
    [driverRoleId, removeRole],
  );

  /**
   * Toggle a driver attribute on or off.
   * The mutation handles null config by initializing to {} first, then appending.
   */
  const setDriverAttribute = useCallback(
    async (
      driverId: number,
      userId: number,
      currentAttributes: Attributes,
      key: MobilityAttribute,
      enabled: boolean,
    ) => {
      const newAttributes = { ...currentAttributes, [key]: enabled };
      await updateDriverConfig({
        variables: {
          driverId,
          config: { attributes: newAttributes },
        },
        ...refetchUser(userId),
      });
    },
    [updateDriverConfig],
  );

  /** Deactivate user (sets active = false). */
  const deactivateUser = useCallback(
    async (userId: number) => {
      await terminateUser({
        variables: { userId },
        ...refetchUser(userId),
      });
    },
    [terminateUser],
  );

  /** Reactivate user (sets active = true). */
  const reactivateUser = useCallback(
    async (userId: number) => {
      await reinstateUser({
        variables: { userId },
        ...refetchUser(userId),
      });
    },
    [reinstateUser],
  );

  /** Sync permissions: auto-promote/demote role based on customer count, sync usertoroles, refresh claims. */
  const syncPermissions = useCallback(
    async (userId: number, currentRole: string, customerCount: number) => {
      const { calculateTargetRole } = await import('../utils/roleUtils');
      const targetRole = calculateTargetRole(currentRole, customerCount);
      if (targetRole) {
        await updateUser({
          variables: { userId, userObj: { default_role: targetRole } },
          ...refetchUser(userId),
        });
      }
      await syncRoles(userId);
      await refreshClaims({ variables: { userId } });
    },
    [updateUser, syncRoles, refreshClaims],
  );

  return {
    assignRole,
    grantDriverAccess,
    revokeDriverAccess,
    setDriverAttribute,
    deactivateUser,
    reactivateUser,
    syncPermissions,
    loading: {
      update: updateLoading,
      refresh: refreshLoading,
      deactivate: deactivateLoading,
      reactivate: reactivateLoading,
    },
  };
}
